import type {
  MailPlaceholderDefinition,
  MailPlaceholderValueType,
} from "api/mailTemplates/MailTemplate.typed";
import {
  MAIL_PLACEHOLDER_CATEGORY_BODY_FRAGMENT,
  MAIL_PLACEHOLDER_CATEGORY_STANDARD,
  MAIL_PLACEHOLDER_TYPE_BOOLEAN,
  MAIL_PLACEHOLDER_TYPE_EMAIL,
  MAIL_PLACEHOLDER_TYPE_PHONE,
  MAIL_PLACEHOLDER_TYPE_STRING,
  MAIL_PLACEHOLDER_TYPE_URL,
} from "api/mailTemplates/MailTemplate.typed";

export function mailPlaceholderDefByKey(
  catalog: readonly MailPlaceholderDefinition[],
  key: string,
): MailPlaceholderDefinition | undefined {
  return catalog.find((d) => d.key === key);
}

export function mailPlaceholderKeyAllowedForValueTypes(
  catalog: readonly MailPlaceholderDefinition[],
  key: string,
  allowed: ReadonlySet<MailPlaceholderValueType>,
  options?: { allowUnknownKey?: boolean },
): boolean {
  const def = mailPlaceholderDefByKey(catalog, key);

  if (!def) {
    return options?.allowUnknownKey ?? false;
  }

  return allowed.has(def.type);
}

export function mailPlaceholderKeyInDefinitionList(
  key: string,
  allowed: readonly MailPlaceholderDefinition[],
): boolean {
  return allowed.some((d) => d.key === key);
}

export type MailPlaceholderInsertTarget = "body" | "subject" | "to" | "bcc";

export type MailPhHrefScheme = "mailto" | "tel";

const MAIL_PH_HREF_SCHEME_BY_TYPE: Partial<Record<MailPlaceholderValueType, MailPhHrefScheme>> = {
  [MAIL_PLACEHOLDER_TYPE_EMAIL]: "mailto",
  [MAIL_PLACEHOLDER_TYPE_PHONE]: "tel",
};

export function mailPlaceholderBodyHrefScheme(
  key: string,
  catalog?: readonly MailPlaceholderDefinition[],
): MailPhHrefScheme | undefined {
  if (!catalog || catalog.length === 0) {
    return undefined;
  }

  const def = mailPlaceholderDefByKey(catalog, key);

  if (!def) {
    return undefined;
  }

  return MAIL_PH_HREF_SCHEME_BY_TYPE[def.type];
}

const MAIL_ENVELOPE_SUBJECT_TYPES = new Set<MailPlaceholderValueType>([
  MAIL_PLACEHOLDER_TYPE_STRING,
  MAIL_PLACEHOLDER_TYPE_EMAIL,
  MAIL_PLACEHOLDER_TYPE_PHONE,
]);

export function mailEnvelopeSubjectPlaceholderChips(
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderDefinition[] {
  return catalog.filter((d) => {
    if (!MAIL_ENVELOPE_SUBJECT_TYPES.has(d.type)) {
      return false;
    }

    const cat = d.category ?? MAIL_PLACEHOLDER_CATEGORY_STANDARD;

    return (
      cat === MAIL_PLACEHOLDER_CATEGORY_STANDARD || cat === MAIL_PLACEHOLDER_CATEGORY_BODY_FRAGMENT
    );
  });
}

export function mailEnvelopeRecipientPlaceholderChips(
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderDefinition[] {
  return catalog.filter((d) => d.type === MAIL_PLACEHOLDER_TYPE_EMAIL);
}

export function mailPlaceholderValueTypesForRichBody(): ReadonlySet<MailPlaceholderValueType> {
  return new Set([
    MAIL_PLACEHOLDER_TYPE_STRING,
    MAIL_PLACEHOLDER_TYPE_URL,
    MAIL_PLACEHOLDER_TYPE_EMAIL,
    MAIL_PLACEHOLDER_TYPE_PHONE,
  ]);
}

export function mailPlaceholderValueTypesForConditionKeyField(): ReadonlySet<MailPlaceholderValueType> {
  return new Set([MAIL_PLACEHOLDER_TYPE_BOOLEAN]);
}

export function mailPlaceholderValueTypesForSubject(): ReadonlySet<MailPlaceholderValueType> {
  return MAIL_ENVELOPE_SUBJECT_TYPES;
}

export function mailPlaceholderValueTypesForLinkHrefField(): ReadonlySet<MailPlaceholderValueType> {
  return new Set([MAIL_PLACEHOLDER_TYPE_URL]);
}

export function mailLinkHrefPlaceholderChips(
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderDefinition[] {
  return catalog.filter((d) => d.type === MAIL_PLACEHOLDER_TYPE_URL);
}

export function mailBodyFragmentInsertPlaceholderChips(
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderDefinition[] {
  return catalog.filter(
    (d) =>
      d.category === MAIL_PLACEHOLDER_CATEGORY_BODY_FRAGMENT &&
      d.type !== MAIL_PLACEHOLDER_TYPE_BOOLEAN,
  );
}

export function mailPlaceholderChipsForInsertTarget(
  catalog: readonly MailPlaceholderDefinition[],
  target: MailPlaceholderInsertTarget,
): MailPlaceholderDefinition[] {
  switch (target) {
    case "subject":
      return mailEnvelopeSubjectPlaceholderChips(catalog);
    case "to":
    case "bcc":
      return mailEnvelopeRecipientPlaceholderChips(catalog);
    default:
      return mailBodyFragmentInsertPlaceholderChips(catalog);
  }
}

export function mailPlaceholderPaletteChips(
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderDefinition[] {
  const body = mailBodyFragmentInsertPlaceholderChips(catalog);
  const emails = mailEnvelopeRecipientPlaceholderChips(catalog);
  const seen = new Set(body.map((d) => d.key));
  const extra = emails.filter((d) => !seen.has(d.key));

  return [...body, ...extra];
}

export function mailPlaceholderChipAllowDrag(target: MailPlaceholderInsertTarget): boolean {
  return target === "body";
}

export function mailPlaceholderValueTypesForFreeformTemplate(): ReadonlySet<MailPlaceholderValueType> {
  return new Set([
    MAIL_PLACEHOLDER_TYPE_STRING,
    MAIL_PLACEHOLDER_TYPE_EMAIL,
    MAIL_PLACEHOLDER_TYPE_PHONE,
    MAIL_PLACEHOLDER_TYPE_URL,
    MAIL_PLACEHOLDER_TYPE_BOOLEAN,
  ]);
}

export function mailFreeformTemplatePlaceholderChips(
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderDefinition[] {
  return [...catalog];
}

export function mailPlaceholderInteractiveKeys(
  catalog: readonly MailPlaceholderDefinition[],
  target: MailPlaceholderInsertTarget,
): Set<string> {
  const keys = new Set(mailPlaceholderChipsForInsertTarget(catalog, target).map((d) => d.key));

  for (const d of mailEnvelopeRecipientPlaceholderChips(catalog)) {
    keys.add(d.key);
  }

  return keys;
}

export type MailPlaceholderInsertKind = "to" | "bcc" | "subject" | "body";

export function mailPlaceholderInsertKind(
  def: MailPlaceholderDefinition | undefined,
  target: MailPlaceholderInsertTarget,
  lastRecipientField: "to" | "bcc",
  catalog: readonly MailPlaceholderDefinition[],
): MailPlaceholderInsertKind | null {
  if (def?.type === MAIL_PLACEHOLDER_TYPE_EMAIL) {
    if (target === "to" || target === "bcc") {
      return target;
    }

    if (target === "subject") {
      return "subject";
    }

    if (def.category === MAIL_PLACEHOLDER_CATEGORY_BODY_FRAGMENT) {
      return "body";
    }

    return lastRecipientField;
  }

  const allowed = mailPlaceholderChipsForInsertTarget(catalog, target);

  if (!allowed.some((d) => d.key === def?.key)) {
    return null;
  }

  if (target === "subject") {
    return "subject";
  }

  return "body";
}
